/**
 * Recent-search history, persisted to localStorage.
 *
 * Most-recent-first, case-insensitively de-duplicated, capped. Every storage access is
 * guarded: Safari private mode and cookie-blocked embeds throw on access rather than
 * returning null, and the legacy build swallowed that the same way.
 */
const HISTORY_KEY = 'atlas_search_history';
const HISTORY_MAX = 6;

export class SearchHistory {
  #entries: string[] = [];

  constructor(private readonly storage: Storage | null = safeStorage()) {
    this.load();
  }

  get entries(): readonly string[] {
    return this.#entries;
  }

  load(): void {
    try {
      const raw = this.storage?.getItem(HISTORY_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      this.#entries = Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      this.#entries = [];
    }
  }

  save(): void {
    try {
      this.storage?.setItem(HISTORY_KEY, JSON.stringify(this.#entries));
    } catch {
      /* storage unavailable — history is best-effort */
    }
  }

  /** Add a query to the front, removing any prior case-insensitive duplicate. */
  push(query: string): void {
    const q = query.trim();
    if (!q) return;
    const lower = q.toLowerCase();
    this.#entries = [q, ...this.#entries.filter((x) => x.toLowerCase() !== lower)].slice(0, HISTORY_MAX);
    this.save();
  }

  clear(): void {
    this.#entries = [];
    this.save();
  }
}

function safeStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}
